import React, { useState, useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { 
  TicketIcon, Clock, CheckCircle2, AlertTriangle, Users, TrendingUp,
  BarChart3, PieChart, Timer, ArrowUpRight, ArrowDownRight
} from 'lucide-react';
import { toast } from 'sonner';

export default function TicketAnalytics() {
  const { API } = useAuth();
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState('30');
  const [analytics, setAnalytics] = useState(null);

  useEffect(() => {
    fetchAnalytics();
  }, [period]);

  const fetchAnalytics = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/tickets/analytics/summary?days=${period}`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });
      const data = await res.json();
      setAnalytics(data);
    } catch (error) {
      toast.error('Failed to fetch analytics');
    } finally {
      setLoading(false);
    }
  };

  if (loading || !analytics) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="animate-spin h-8 w-8 border-4 border-emerald-500 border-t-transparent rounded-full" />
      </div>
    );
  }

  const statusColors = {
    open: 'bg-blue-500',
    in_progress: 'bg-amber-500',
    pending_customer: 'bg-purple-500',
    resolved: 'bg-emerald-500',
    closed: 'bg-slate-400'
  };

  const categoryColors = {
    connectivity: 'bg-red-500',
    billing: 'bg-green-500',
    technical: 'bg-blue-500',
    service_request: 'bg-purple-500',
    iptv_ott: 'bg-orange-500',
    general: 'bg-slate-500'
  };

  const priorityColors = {
    critical: 'bg-red-500',
    high: 'bg-orange-500',
    medium: 'bg-blue-500',
    low: 'bg-slate-400'
  };

  // Calculate percentages for charts
  const totalByStatus = Object.values(analytics.by_status || {}).reduce((a, b) => a + b, 0);
  const totalByCategory = Object.values(analytics.by_category || {}).reduce((a, b) => a + b, 0);
  const totalByPriority = Object.values(analytics.by_priority || {}).reduce((a, b) => a + b, 0);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Ticket Analytics</h1>
          <p className="text-slate-500">Monitor support performance and SLA compliance</p>
        </div>
        <Select value={period} onValueChange={setPeriod}>
          <SelectTrigger className="w-40">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="7">Last 7 days</SelectItem>
            <SelectItem value="30">Last 30 days</SelectItem>
            <SelectItem value="90">Last 90 days</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Key Metrics */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-slate-500 uppercase tracking-wide">Total Tickets</p>
                <p className="text-3xl font-bold text-slate-900">{analytics.total_tickets}</p>
              </div>
              <div className="h-12 w-12 bg-slate-100 rounded-full flex items-center justify-center">
                <TicketIcon className="h-6 w-6 text-slate-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-emerald-200 bg-emerald-50/50">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-emerald-600 uppercase tracking-wide">SLA Compliance</p>
                <p className="text-3xl font-bold text-emerald-700">{analytics.sla_compliance?.compliance_rate || 0}%</p>
                <p className="text-xs text-emerald-600 mt-1">
                  {analytics.sla_compliance?.met || 0} met / {analytics.sla_compliance?.breached || 0} breached
                </p>
              </div>
              <div className="h-12 w-12 bg-emerald-200 rounded-full flex items-center justify-center">
                <CheckCircle2 className="h-6 w-6 text-emerald-700" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-amber-200 bg-amber-50/50">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-amber-600 uppercase tracking-wide">Avg Resolution</p>
                <p className="text-3xl font-bold text-amber-700">{analytics.avg_resolution_hours || 0}h</p>
              </div>
              <div className="h-12 w-12 bg-amber-200 rounded-full flex items-center justify-center">
                <Timer className="h-6 w-6 text-amber-700" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-red-200 bg-red-50/50">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-red-600 uppercase tracking-wide">Escalation Rate</p>
                <p className="text-3xl font-bold text-red-700">{analytics.escalation_rate || 0}%</p>
              </div>
              <div className="h-12 w-12 bg-red-200 rounded-full flex items-center justify-center">
                <AlertTriangle className="h-6 w-6 text-red-700" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Charts Row */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {/* By Status */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2">
              <PieChart className="h-4 w-4" /> By Status
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {Object.entries(analytics.by_status || {}).map(([status, count]) => (
                <div key={status} className="space-y-1">
                  <div className="flex justify-between text-sm">
                    <span className="capitalize">{status.replace('_', ' ')}</span>
                    <span className="font-medium">{count}</span>
                  </div>
                  <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
                    <div 
                      className={`h-full ${statusColors[status] || 'bg-slate-400'} transition-all`}
                      style={{ width: `${totalByStatus ? (count / totalByStatus * 100) : 0}%` }}
                    />
                  </div>
                </div>
              ))}
              {Object.keys(analytics.by_status || {}).length === 0 && (
                <p className="text-sm text-slate-500 text-center py-4">No data</p>
              )}
            </div>
          </CardContent>
        </Card>

        {/* By Category */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2">
              <BarChart3 className="h-4 w-4" /> By Category
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {Object.entries(analytics.by_category || {}).map(([category, count]) => (
                <div key={category} className="space-y-1">
                  <div className="flex justify-between text-sm">
                    <span className="capitalize">{category.replace('_', ' ')}</span>
                    <span className="font-medium">{count}</span>
                  </div>
                  <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
                    <div 
                      className={`h-full ${categoryColors[category] || 'bg-slate-400'} transition-all`}
                      style={{ width: `${totalByCategory ? (count / totalByCategory * 100) : 0}%` }}
                    />
                  </div>
                </div>
              ))}
              {Object.keys(analytics.by_category || {}).length === 0 && (
                <p className="text-sm text-slate-500 text-center py-4">No data</p>
              )}
            </div>
          </CardContent>
        </Card>

        {/* By Priority */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2">
              <TrendingUp className="h-4 w-4" /> By Priority
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {Object.entries(analytics.by_priority || {}).map(([priority, count]) => (
                <div key={priority} className="space-y-1">
                  <div className="flex justify-between text-sm">
                    <span className="capitalize">{priority}</span>
                    <span className="font-medium">{count}</span>
                  </div>
                  <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
                    <div 
                      className={`h-full ${priorityColors[priority] || 'bg-slate-400'} transition-all`}
                      style={{ width: `${totalByPriority ? (count / totalByPriority * 100) : 0}%` }}
                    />
                  </div>
                </div>
              ))}
              {Object.keys(analytics.by_priority || {}).length === 0 && (
                <p className="text-sm text-slate-500 text-center py-4">No data</p>
              )}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Staff Performance */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Users className="h-5 w-5" /> Staff Performance
          </CardTitle>
          <CardDescription>Ticket handling metrics by team member</CardDescription>
        </CardHeader>
        <CardContent>
          {(analytics.staff_performance || []).length === 0 ? (
            <p className="text-sm text-slate-500 text-center py-8">No staff performance data yet</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b">
                    <th className="text-left py-3 px-4 text-sm font-medium text-slate-500">Staff Member</th>
                    <th className="text-center py-3 px-4 text-sm font-medium text-slate-500">Handled</th>
                    <th className="text-center py-3 px-4 text-sm font-medium text-slate-500">Resolved</th>
                    <th className="text-center py-3 px-4 text-sm font-medium text-slate-500">SLA Met</th>
                    <th className="text-center py-3 px-4 text-sm font-medium text-slate-500">Performance</th>
                  </tr>
                </thead>
                <tbody>
                  {analytics.staff_performance.map((staff, idx) => {
                    const resolutionRate = staff.total_handled > 0 
                      ? Math.round(staff.resolved / staff.total_handled * 100) 
                      : 0;
                    const slaRate = staff.total_handled > 0 
                      ? Math.round(staff.sla_met / staff.total_handled * 100) 
                      : 0;
                    
                    return (
                      <tr key={idx} className="border-b hover:bg-slate-50">
                        <td className="py-3 px-4">
                          <span className="font-medium">{staff._id || 'Unknown'}</span>
                        </td>
                        <td className="py-3 px-4 text-center">{staff.total_handled}</td>
                        <td className="py-3 px-4 text-center">
                          <span className="text-emerald-600">{staff.resolved}</span>
                        </td>
                        <td className="py-3 px-4 text-center">
                          <span className="text-blue-600">{staff.sla_met}</span>
                        </td>
                        <td className="py-3 px-4 text-center">
                          <div className="flex items-center justify-center gap-2">
                            <Badge className={slaRate >= 80 ? 'bg-emerald-100 text-emerald-700' : slaRate >= 50 ? 'bg-amber-100 text-amber-700' : 'bg-red-100 text-red-700'}>
                              {slaRate}% SLA
                            </Badge>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
